import { Box, Button as MuiButton, Stack, Typography, useTheme } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as EditIcon } from "Components/_Shared/Icons/Assets/edit.svg";
import { useListImmovableAssetContracts, useListMovableAssetContracts } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Amount, ImmovableAssetItemImmovableSubType, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { ContractDrawer } from "./ContractDrawer";
import { ContractTile } from "./ContractTile";

type Props = {
  readonly itemId: string;
  readonly name: string;
  readonly note?: string | null;
  readonly assetType: "immovable" | "movable";
  readonly assetSubType: ImmovableAssetItemImmovableSubType | MovableAssetItemMovableSubType;
  readonly propertyInsurance?: Amount | null;
  readonly onEditAsset: () => void;
};

export const ContractsOverview: React.FC<Props> = ({ itemId, name, note, assetType, assetSubType, propertyInsurance, onEditAsset }) => {
  const theme = useTheme();
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const { data: immovableOverview } = useListImmovableAssetContracts(analysisId, versionId, itemId, {
    query: { enabled: assetType === "immovable" },
  });
  const { data: movableOverview } = useListMovableAssetContracts(analysisId, versionId, itemId, {
    query: { enabled: assetType === "movable" },
  });

  const overview = assetType === "immovable" ? immovableOverview : movableOverview;
  const currentSolutions = overview?.currentSolutions ?? [];
  const proposedSolutions = overview?.proposedSolutions ?? [];

  const [contractType, setContractType] = useState<"current" | "proposed" | undefined>(undefined);
  const [contractDrawerOpen, setContractDrawerOpen] = useState(false);
  const [assetContractId, setAssetContractId] = useState<string | undefined>(undefined);

  return (
    <>
      <Stack spacing={Spacing.XXL}>
        <Stack spacing={Spacing.XL}>
          <Flex justifyContent="space-between">
            <Box>
              <Typography display="block" variant="subtitle1" fontWeight={600}>
                {name}
              </Typography>
              {!!note && (
                <Typography color="secondary" variant="caption">
                  {note}
                </Typography>
              )}
            </Box>
            <HStack spacing={Spacing.S}>
              <MuiButton
                style={{ paddingLeft: theme.spacing(Spacing.L), paddingRight: theme.spacing(Spacing.L) }}
                variant="text"
                color="secondary"
                disabled={readOnly}
                startIcon={<EditIcon width={18} height={18} />}
                onClick={onEditAsset}
              >
                {t(Resources.Common.Edit)}
              </MuiButton>
              <MuiButton
                style={{ paddingLeft: theme.spacing(Spacing.L), paddingRight: theme.spacing(Spacing.L) }}
                variant="text"
                color="primary"
                disabled={readOnly}
                startIcon={<AddIcon width={18} height={18} />}
                onClick={() => {
                  setContractType(undefined);
                  setAssetContractId(undefined);
                  setContractDrawerOpen(true);
                }}
              >
                {t(Resources.AnalysisDetail.AssetsAndContracts.AddContract)}
              </MuiButton>
            </HStack>
          </Flex>
          {propertyInsurance && (
            <Box>
              <Typography color="secondary" variant="caption">
                {t(Resources.AnalysisDetail.AssetsAndContracts.InsuranceValue)}
              </Typography>
              &nbsp;
              <Typography fontWeight={600} variant="caption">
                {formatCurrency(propertyInsurance.value, propertyInsurance.currencyCode)}
              </Typography>
            </Box>
          )}
        </Stack>
        <Stack spacing={Spacing.M}>
          <Typography fontWeight={600}>{t(Resources.AnalysisDetail.AssetsAndContracts.CurrentInsurance)}</Typography>
          {currentSolutions.map((cs) => (
            <ContractTile
              guid={cs.guid}
              labels={cs.labels}
              step={cs.step}
              insuranceAmount={cs.insuranceAmount}
              key={cs.guid}
              name={cs.name}
              onEdit={(guid) => {
                setContractType("current");
                setAssetContractId(guid);
                setContractDrawerOpen(true);
              }}
            />
          ))}
        </Stack>
        <Stack spacing={Spacing.M}>
          <Typography fontWeight={600}>{t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolution)}</Typography>
          {proposedSolutions.map((cs) => (
            <ContractTile
              key={cs.guid}
              guid={cs.guid}
              labels={cs.labels}
              step={cs.step}
              insuranceAmount={cs.insuranceAmount}
              name={cs.name}
              onEdit={(guid) => {
                setContractType("proposed");
                setAssetContractId(guid);
                setContractDrawerOpen(true);
              }}
            />
          ))}
        </Stack>
      </Stack>
      <ContractDrawer
        assetContractId={assetContractId}
        assetItemId={itemId}
        assetType={assetType}
        assetSubType={assetSubType}
        contractType={contractType}
        onClose={() => {
          setContractDrawerOpen(false);
          setAssetContractId(undefined);
        }}
        open={contractDrawerOpen}
      />
    </>
  );
};
